import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Plus, MoreHorizontal, X, LayoutList, Rows3 } from "lucide-react";
import FlashPrice from "./FlashPrice";

const fmtPrice = (p) => {
  if (p == null || Number.isNaN(p)) return "—";
  if (Math.abs(p) >= 1000) return p.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  if (Math.abs(p) >= 1) return p.toFixed(2);
  return p.toFixed(4);
};
const fmtChg = (c) => (c == null || Number.isNaN(c) ? "—" : `${c >= 0 ? "+" : ""}${fmtPrice(c)}`);
const fmtPct = (p) => (p == null || Number.isNaN(p) ? "—" : `${p >= 0 ? "+" : ""}${p.toFixed(2)}%`);
const signClass = (v) => (v > 0 ? "text-emerald-400" : v < 0 ? "text-rose-400" : "text-zinc-400");

function Row({ base, quote, selected, compact, onSelect, onRemove, marked }) {
  const [menu, setMenu] = useState(false);
  const chg = quote?.chg;
  const chgPct = quote?.chgPct;
  return (
    <div
      onClick={() => onSelect(base)}
      onContextMenu={(e) => {
        e.preventDefault();
        setMenu((m) => !m);
      }}
      className={`group relative grid grid-cols-[1fr_auto_auto] items-center gap-2 px-2 cursor-pointer text-xs
        ${compact ? "h-[22px]" : "h-9"}
        ${selected ? "bg-zinc-700/40 outline outline-1 outline-emerald-500/40" : "hover:bg-zinc-800/60"}`}
    >
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="w-4 h-4 rounded-full bg-zinc-700 text-[8px] flex items-center justify-center text-zinc-300 flex-shrink-0">
          {base[0]}
        </span>
        <span className="truncate font-medium text-zinc-200" title={`${base} / Tether · OKX`}>
          {base}
        </span>
        {marked ? <i className="wl-pos-dot" title="Open AEON position" aria-label="Open AEON position" /> : null}
      </div>
      <FlashPrice value={quote?.last} format={fmtPrice} className="text-right text-zinc-100" />
      <div className={`text-right ${compact ? "w-14" : "w-16"} ${signClass(chgPct)}`}>
        <div className="tabular-nums">{fmtPct(chgPct)}</div>
        {!compact && <div className="tabular-nums text-[10px] opacity-80">{fmtChg(chg)}</div>}
      </div>
      {menu && (
        <div
          className="absolute right-2 top-full z-20 mt-0.5 bg-zinc-900 border border-zinc-700 rounded shadow-lg text-xs"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="flex items-center gap-2 px-3 py-1.5 hover:bg-zinc-800 w-full text-left text-rose-300"
            onClick={() => {
              onRemove(base);
              setMenu(false);
            }}
          >
            <X className="w-3 h-3" /> Remove
          </button>
        </div>
      )}
    </div>
  );
}

export default function Watchlist({ list, quotes, selected, onSelect, onAddSymbol, onRemoveSymbol, onToggleSection, markSymbols = [] }) {
  const [compact, setCompact] = useState(true);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");

  const sections = list?.sections || [];
  const add = () => {
    const s = draft.trim().toUpperCase();
    if (s) onAddSymbol(s);
    setDraft("");
    setAdding(false);
  };

  return (
    <div className="flex flex-col h-full bg-zinc-900/40 border-l border-zinc-800 text-zinc-300 select-none">
      {/* Header */}
      <div className="flex items-center justify-between px-2 h-9 border-b border-zinc-800 flex-shrink-0">
        <button className="flex items-center gap-1 text-xs font-semibold text-zinc-200">
          {list?.name || "Watchlist"} <ChevronDown className="w-3 h-3" />
        </button>
        <div className="flex items-center gap-1">
          <button title="Add symbol" onClick={() => setAdding((a) => !a)} className="p-1 hover:bg-zinc-800 rounded">
            <Plus className="w-3.5 h-3.5" />
          </button>
          <button title="Toggle layout" onClick={() => setCompact((c) => !c)} className="p-1 hover:bg-zinc-800 rounded">
            {compact ? <Rows3 className="w-3.5 h-3.5" /> : <LayoutList className="w-3.5 h-3.5" />}
          </button>
          <button title="More" className="p-1 hover:bg-zinc-800 rounded">
            <MoreHorizontal className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {adding && (
        <div className="flex items-center gap-1 px-2 py-1.5 border-b border-zinc-800">
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="Add ticker e.g. LINK"
            className="flex-1 bg-zinc-800 text-xs px-2 py-1 rounded outline-none text-zinc-100 uppercase"
          />
          <button onClick={add} className="text-xs px-2 py-1 bg-emerald-600/80 rounded text-white">
            Add
          </button>
        </div>
      )}

      {/* Column header */}
      <div className="grid grid-cols-[1fr_auto_auto] gap-2 px-2 py-1 text-[10px] uppercase text-zinc-500 border-b border-zinc-800/60">
        <span>Symbol</span>
        <span className="text-right">Last</span>
        <span className={`text-right ${compact ? "w-14" : "w-16"}`}>Chg%</span>
      </div>

      {/* Sections */}
      <div className="flex-1 overflow-y-auto">
        {markSymbols.length ? (
          <div>
            <div className="flex items-center gap-1 w-full px-2 py-1 text-[10px] font-semibold uppercase text-zinc-500">
              Open positions <span className="ml-1 opacity-60">{markSymbols.length}</span>
            </div>
            {markSymbols.map((b) => (
              <Row key={`pos-${b}`} base={b} quote={quotes[b]} compact={compact} selected={selected === b}
                onSelect={onSelect} onRemove={() => {}} marked />
            ))}
          </div>
        ) : null}
        {sections.map((sec) => (
          <div key={sec.id}>
            <button
              onClick={() => onToggleSection(sec.id)}
              className="flex items-center gap-1 w-full px-2 py-1 text-[10px] font-semibold uppercase text-zinc-500 hover:bg-zinc-800/40"
            >
              {sec.collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {sec.name}
              <span className="ml-1 opacity-60">{sec.symbols?.length || 0}</span>
            </button>
            {!sec.collapsed &&
              (sec.symbols || []).map((base) => (
                <Row
                  key={base}
                  base={base}
                  quote={quotes[base]}
                  compact={compact}
                  selected={selected === base}
                  onSelect={onSelect}
                  marked={markSymbols.includes(base)}
                  onRemove={(b) => onRemoveSymbol(sec.id, b)}
                />
              ))}
          </div>
        ))}
      </div>
    </div>
  );
}
